// src/components/issues/BoutonSuivreElement.tsx — SCE Issues
// Cloche « Suivre » de la fiche projet (Lot 12 bis) et de la fiche
// équipement (Lot 12 ter).
//
// Suivre un PN ou un N° de série = être prévenu de chaque NOUVEAU problème
// déclaré dessus (flow 1, selon le canal choisi dans Mes notifications) et
// voir ses problèmes actifs dans « Éléments suivis » sur l'accueil. Manuel :
// personne n'est abonné d'office.
//
// Pas de compteur : savoir combien de personnes suivent un élément
// demanderait de lire les préférences de tout le monde.
//
// Bascule de rôle : l'état de la personne affichée, en lecture seule.

import { useState } from 'react'
import { BoutonCloche } from '@/components/ui/BoutonCloche'
import { useSnackbar } from '@/components/ui/Snackbar'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useElementsSuivis } from '@/hooks/useElementsSuivis'
import { listeDuGenre, suit, type GenreSuivi } from '@/utils/elementsSuivis'

const EXPLICATIONS: Record<GenreSuivi, string> = {
  projet: 'Prévenu de chaque nouveau problème déclaré sur ce projet',
  equipement: 'Prévenu de chaque nouveau problème déclaré sur cet équipement',
}

const MESSAGE_ERREUR: Record<GenreSuivi, string> = {
  projet: 'Suivi du projet impossible.',
  equipement: 'Suivi de l’équipement impossible.',
}

export function BoutonSuivreElement({ genre, id }: { genre: GenreSuivi; id: string }): JSX.Element | null {
  const { user, isFournisseur, enVueRole } = useCurrentUser()
  const snackbar = useSnackbar()
  const { elements, lus, changer } = useElementsSuivis(user?.upn)
  const [enCours, setEnCours] = useState(false)

  if (!user || isFournisseur) return null

  const suis = suit(listeDuGenre(elements, genre), id)
  const explication = EXPLICATIONS[genre]

  async function basculer(): Promise<void> {
    if (enVueRole || enCours || !lus) return
    setEnCours(true)
    try {
      await changer(genre, id, !suis)
      snackbar.show(
        suis
          ? `Vous ne suivez plus ${id}.`
          : `Vous suivez ${id} : ses problèmes actifs apparaissent dans « Éléments suivis » sur votre accueil.`,
        'success',
      )
    } catch (err: unknown) {
      snackbar.show(err instanceof Error ? err.message : MESSAGE_ERREUR[genre], 'error')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <BoutonCloche
      suis={suis}
      enCours={enCours}
      indisponible={!lus}
      lectureSeule={enVueRole}
      ariaLabel={suis ? `Ne plus suivre ${id}` : `Suivre ${id} — ${explication.toLowerCase()}`}
      titre={enVueRole ? 'Lecture seule en vue d’un autre profil' : explication}
      onClick={() => void basculer()}
    />
  )
}
